<template>
	<div
		class="buybox-info-modal-wrapper"
		:class="{ 'buybox-info-modal-wrapper-visible': displayBuyBoxInfoModal }"
		v-if="hasInfo"
	>
		<BaseModalNew :disableTeleport="!displayBuyBoxInfoModal" @close="emit('close', $event)">
			<template #title>{{ heading }}</template>
			<div class="buybox-info-modal">
				<div v-if="availableUntil" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_AVAIL_UNTIL') }}</p>
					<p class="buybox-info-modal__row__cell">{{ availableUntil }}</p>
				</div>
				<div v-if="runtimeMins" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_RUNTIME') }}</p>
					<p class="buybox-info-modal__row__cell">{{ runtimeMins }}</p>
				</div>
				<div v-if="ageRating && !isDvdBluRayOffer" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_AGERATING') }}</p>
					<p class="buybox-info-modal__row__cell">{{ ageRating }}</p>
				</div>
				<div v-if="streamQuality" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_QUALITY') }}</p>
					<p class="buybox-info-modal__row__cell">{{ streamQuality }}</p>
				</div>
				<div v-if="audioLanguages" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_AUDIO_LANGUAGES') }}</p>
					<p class="buybox-info-modal__row__cell">{{ audioLanguages }}</p>
				</div>
				<div v-if="subtitleLanguages" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_SUBTITLE_LANGUAGES') }}</p>
					<p class="buybox-info-modal__row__cell">{{ subtitleLanguages }}</p>
				</div>
				<div v-if="audioTechnology" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_AUDIO_TECHNOLOGY') }}</p>
					<p class="buybox-info-modal__row__cell">{{ audioTechnology }}</p>
				</div>
				<div v-if="videoTechnology" class="buybox-info-modal__row">
					<p class="buybox-info-modal__row__head">{{ $t('WEBAPP_VIDEO_TECHNOLOGY') }}</p>
					<p class="buybox-info-modal__row__cell">{{ videoTechnology }}</p>
				</div>
			</div>
		</BaseModalNew>
	</div>
</template>

<script setup lang="ts">
import {
	AudioTechnology,
	MonetizationType,
	PresentationType,
	UpcomingRelease,
	VideoTechnology,
} from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

const BaseModalNew = defineAsyncComponent(() => import('@/components/modals/BaseModalNew.vue'));

const { t, te } = useI18n();
const { country, language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: TitleDetail;
	offer: (TitleOfferFragment & { iconUrl?: string; bundleId?: number; bundleClearName?: string }) | UpcomingRelease;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	/** For SEO - persist elements in HTML using css styles, not inline styles from v-show */
	displayBuyBoxInfoModal?: boolean;
	bundlePresentationLabel?: string | null;
}>();

const emit = defineEmits<{
	(e: 'close', display: boolean): void;
}>();

const videoTechnologyMapping = {
	DOLBY_VISION: 'Dolby Vision',
	HDR: 'HDR',
	HDR10: 'HDR10',
	HDR10_PLUS: 'HDR10+',
};

const audioTechnologyMapping = {
	DOLBY_5_POINT_1: 'Dolby 5.1',
	DOLBY_ATMOS: 'Dolby Atmos',
	_5_POINT_1: 'Generic 5.1',
};

const providerName = computed(() => {
	if ('bundleClearName' in props.offer && props.offer?.bundleClearName) return props.offer.bundleClearName;
	return props.offer?.package?.clearName;
});

const titleName = computed(() => {
	if (props.seasonNumber && props.episodeNumber)
		return `${props.title.content.title} - ${t('WEBAPP_SEASON_X_EPISODE_Y', {
			season: props.seasonNumber,
			episode: props.episodeNumber,
		})}`;

	const isSeason = props.title.__typename === 'Season' && !!props.title.show;
	return isSeason ? `${props.title.show.content.title} - ${props.title.content.title}` : props.title.content.title;
});

const isCinema = computed(() => {
	return props.offer.__typename === 'UpcomingRelease'
		? props.offer.releaseType === 'THEATRICAL'
		: (props.offer as TitleOfferFragment).monetizationType === MonetizationType.Cinema;
});

const heading = computed(() => {
	if (props.offer.__typename === 'Offer') {
		if (props.offer.presentationType === PresentationType.Dvd) {
			return t('WEBAPP_MORE_INFO_DVD', { title: titleName.value, provider: providerName.value });
		}
		if (props.offer.presentationType === PresentationType.Bluray) {
			return t('WEBAPP_MORE_INFO_BLURAY', { title: titleName.value, provider: providerName.value });
		}
	}
	if (isCinema.value) {
		return t('WEBAPP_MORE_INFO_TITLE_ON_CINEMA_PROVIDER', {
			titleName: titleName.value,
			provider: providerName.value,
		});
	}
	return t('WEBAPP_MORE_INFO_TITLE_ON_PROVIDER', {
		title: titleName.value,
		provider: providerName.value,
	});
});

const availableUntil = computed(() => {
	if (props.offer.__typename !== 'Offer') return '';
	const date = new Date(props?.offer?.availableTo);
	const isFutureDate = date > new Date();
	if (!isFutureDate) return false;
	const locale = `${language.value}-${country.value}`;
	return date.toLocaleDateString(locale);
});

const runtimeMins = computed(() => {
	if (!props.title.content.runtime) return null;
	return `${props.title.content.runtime}min`;
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season' && props.title.content.ageCertification) {
		return props.title.content.ageCertification;
	}
	return null;
});

const streamQuality = computed(() => {
	if (props.offer.__typename !== 'Offer') return '';
	if (props.bundlePresentationLabel) return props.bundlePresentationLabel;

	switch (props?.offer?.presentationType) {
		case PresentationType.Sd:
			return 'SD';
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'SD';
		case PresentationType.Bluray:
			return 'HD';
		case PresentationType.Bluray_4K:
			return '4K UHD ';
		default:
			return '';
	}
});

const audioLanguages = computed(() =>
	props.offer.__typename === 'Offer' ? languageArrayToString(props.offer?.audioLanguages) : ''
);

const subtitleLanguages = computed(() => {
	return props.offer.__typename === 'Offer' ? languageArrayToString(props?.offer?.subtitleLanguages) : '';
});

const videoTechnology = computed(() => {
	return props.offer.__typename === 'Offer'
		? props.offer?.videoTechnology
				.map((technology: VideoTechnology) => videoTechnologyMapping[technology])
				.join(', ')
		: '';
});

const audioTechnology = computed(() => {
	return props.offer.__typename === 'Offer'
		? props.offer?.audioTechnology
				.map((technology: AudioTechnology) => {
					return audioTechnologyMapping[technology];
				})
				.join(', ')
		: '';
});

const hasInfo = computed(() => {
	return (
		availableUntil.value ||
		runtimeMins.value ||
		ageRating.value ||
		streamQuality.value ||
		audioLanguages.value ||
		subtitleLanguages.value ||
		videoTechnology.value ||
		audioTechnology.value
	);
});

const isDvdBluRayOffer = computed(() => {
	return (
		props.offer.__typename === 'Offer' &&
		[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
			props.offer.presentationType
		)
	);
});

function languageArrayToString(languageArray: string[]) {
	return languageArray
		.map(language => {
			const key = `WEBAPP_LANGUAGE_${language.toUpperCase()}`;
			if (te(key)) {
				return t(key);
			}
			return null;
		})
		.filter(Boolean)
		.join(', ');
}
</script>

<style lang="scss" scoped>
.buybox-info-modal {
	width: 100%;
	p {
		margin: 16px 0;
	}

	&__row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-top: 1px solid #6f7d90;

		&:is(:last-child) {
			border-bottom: 1px solid #6f7d90;
		}

		&__cell,
		&__head {
			color: #fff;
			font-size: 16px;
			line-height: 24px;
			letter-spacing: 0.24px;
		}

		&__head {
			margin-inline-end: 8px;
		}

		&__cell {
			max-width: 75%;
			text-align: right;
		}
	}
}

.modal-small {
	p {
		margin: 8px 0;
	}
	&__cell,
	&__head {
		font-size: 12px;
		line-height: 14px;
	}
}

.buybox-info-modal-wrapper {
	display: none;

	&-visible {
		display: block;
	}
}
</style>
